import { useState, useRef, useEffect } from "react"
import { createTicketType, updateTicketType } from "../../create-edit/createEditApi";

import Modal from "../../../app/universalComponents/Modal";
import Alert from "../../../app/universalComponents/Alert";
import Button from "../../../app/universalComponents/Button";
import SectionLabel from "../../../app/universalComponents/SectionLabel";
import Input from "../../../app/universalComponents/Input";
import Switch from "../../../app/universalComponents/Switch";

function getInitialValues(ticketType) {
    return {
        name: ticketType?.name ?? "",
        price: ticketType?.price ?? "",
        description: ticketType?.description ?? "",
        inventoryCap: ticketType?.inventory_cap ?? "",
        maxBuyAtATime: ticketType?.max_buy ?? "",
        startDate: ticketType?.sales_start_at_date ?? "",
        startTime: ticketType?.sales_start_at_time ?? "",
        endDate: ticketType?.sales_end_at_date ?? "",
        endTime: ticketType?.sales_end_at_time ?? "",
    };
}

export default function TicketTypeModal({ eventId, ticketType, mode, onClose }) {
    console.log(ticketType)

    const [values, setValues] = useState(() => getInitialValues(ticketType));
    const [hasInventoryManagement, setHasInventoryManagement] = useState(ticketType?.inventory_cap != null || ticketType?.max_buy != null);
    const [hasSalesWindow, setHasSalesWindow] = useState(ticketType?.sales_start_at_date != null);
    const [error, setError] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        setValues(getInitialValues(ticketType));
        setIsDirty(false);
        setError(null);
    }, [ticketType]);

    useEffect(() => {
        if (!error) {
            return;
        }
        requestAnimationFrame(() => {
            scrollContainerRef.current?.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        });
    }, [ticketType, error]);

    function handleChange(event) {
        const { name, value } = event.target;
        setValues((current) => ({
            ...current,
            [name]: value,
        }));
        setIsDirty(true);
    }

    function handleInventoryToggle(enabled) {
        setHasInventoryManagement(enabled);
        setIsDirty(true);

        if (!enabled) {
            setValues((current) => ({
                ...current,
                inventoryCap: "",
                maxBuyAtATime: "",
            }));
        }
    }

    function handleSalesWindowToggle(enabled) {
        setHasSalesWindow(enabled);
        setIsDirty(true);

        if (!enabled) {
            setValues((current) => ({
                ...current,
                startDate: "",
                startTime: "",
                endDate: "",
                endTime: "",
            }));
        }
    }

    function handleDiscard() {
        const initialValues =
            mode === "create"
                ? getInitialValues()
                : getInitialValues(ticketType);

        setValues(initialValues);
        setHasInventoryManagement(initialValues.inventoryCap !== "");
        setHasSalesWindow(initialValues.startDate !== "");
        setIsDirty(false);
        setError(null);

        scrollContainerRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setError(null);

        const hasStartDate = Boolean(values.startDate);
        const hasStartTime = Boolean(values.startTime);
        const hasEndDate = Boolean(values.endDate);
        const hasEndTime = Boolean(values.endTime);

        if (hasStartDate !== hasStartTime) {
            setError("Please enter both a start date and a start time.");
            return;
        }

        if (hasEndDate !== hasEndTime) {
            setError("Please enter both an end date and an end time.");
            return;
        }

        try {
            if (mode === "create") {
                await createTicketType(eventId, values);
            } else {
                if (!ticketType?.id) {
                    throw new Error("Missing ticket type ID.");
                }

                await updateTicketType(
                    eventId,
                    ticketType.id,
                    values
                );
            }

            window.location.reload();
        } catch (error) {
            setError(
                error.response?.data?.message ||
                error.message ||
                "Unable to save ticket type."
            );
        } finally {
        }
    }

    return (
        <Modal isOpen={true} onClose={onClose} label={mode === "create" ? "Create New Ticket Batch" : "Edit Ticket Batch"} className="flex flex-col">
            <form onSubmit={handleSubmit} id="ticketTypeDetails" ref={scrollContainerRef} className="flex-1 space-y-4 overflow-y-scroll p-4 md:px-8 py-4">
                {error && (
                    <Alert className="w-full rounded-sm text-center" tone="negative">
                        {error}
                    </Alert>
                )}

                <div>
                    <SectionLabel>Basics</SectionLabel>
                    <fieldset className="grid gap-4 rounded-sm border p-4 md:grid-cols-2">
                        <div className="space-y-4">
                            <Input label="Name" name="name" required value={values.name} placeholder="Early Bird's" onChange={handleChange} />
                            <Input label="Price" name="price" type="number" required prefix="€" value={values.price} placeholder="15.00" onChange={handleChange} />
                        </div>
                        <Input label="Description" name="description" isLarge value={values.description} placeholder="Limited Availability!" onChange={handleChange} />
                    </fieldset>
                </div>

                <div>
                    <div className="flex justify-between">
                        <SectionLabel>Inventory Management</SectionLabel>
                        <Switch checked={hasInventoryManagement} onChange={handleInventoryToggle} label={hasInventoryManagement ? "Managed" : "Unlimited"}/>
                    </div>

                    <fieldset className="grid gap-4 rounded-sm border p-4 grid-cols-2">
                        {hasInventoryManagement ? (
                            <>
                                <Input label="Capacity" name="inventoryCap" required type="number" value={values.inventoryCap} placeholder="120" onChange={handleChange} className="min-w-0 w-full" />
                                <Input label="Max per Buyer" name="maxBuyAtATime" required type="number" value={values.maxBuyAtATime} placeholder="10" onChange={handleChange} className="min-w-0 w-full" />
                            </>
                        ) : (
                            <p className="text-center text-sm text-gray-500 col-span-2">
                                This batch will never sell out.
                            </p>
                        )}
                    </fieldset>
                </div>

                <div>
                    <div className="flex justify-between">
                        <SectionLabel>Sales Window</SectionLabel>
                        <Switch checked={hasSalesWindow} onChange={handleSalesWindowToggle} label={hasSalesWindow ? "Scheduled" : "Always Available"}/>
                    </div>
                    <fieldset className="grid gap-4 rounded-sm border p-4 grid-cols-5">
                        {hasSalesWindow ? (
                            <>
                                <Input label="Start Date" name="startDate" type="date" required value={values.startDate} onChange={handleChange} className="col-span-3" />
                                <Input label="Start Time" name="startTime" type="time" required value={values.startTime} onChange={handleChange} className="col-span-2" />
                                <Input label="End Date" name="endDate" type="date" value={values.endDate} onChange={handleChange} className="col-span-3" />
                                <Input label="End Time" name="endTime" type="time" value={values.endTime} onChange={handleChange} className="col-span-2" />
                            </>
                        ) : (
                            <p className="text-center text-sm text-gray-500 col-span-5">
                                This batch will be on sale all the time
                            </p>
                        )}
                    </fieldset>
                </div>

            </form>

            <footer className="flex items-center justify-between border-t py-2 px-4 md:px-8">
                <p className="text-sm text-gray-500">
                    required{" "}
                    <span className="text-red-500 text-base">*</span>
                </p>

                <div className="flex items-center gap-2">
                    {(isDirty && mode !== "create") && (
                        <span className="text-amber-500">
                            ● <span className="hidden pr-4 text-sm text-gray-500 md:inline">Changes Detected</span>
                        </span>
                    )}

                    <Button type="button" variant="secondary" disabled={!isDirty} onClick={handleDiscard} className="text-sm">
                        Clear Changes
                    </Button>

                    <Button type="submit" form="ticketTypeDetails" disabled={!isDirty} className="text-sm">
                        Save
                    </Button>
                </div>
            </footer>
        </Modal>
    )
}
